import { useEffect, useState } from 'react';
import { selectionProcessesAdmin } from '@/services/selectionProcesses';
import { Skeleton } from '@/components/ui/skeleton';

/* ─── shared table primitives ────────────────────────────────── */
function SectionHeading({ children }) {
  return (
    <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-neutral">
      {children}
    </h2>
  );
}

function SummaryTable({ head, rows, emptyLabel = 'Sem dados' }) {
  if (!rows || rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-neutral">{emptyLabel}</p>
    );
  }
  return (
    <div className="round-dg-wrapper">
      <table
        className="round-dg round-dg--light"
        style={{ borderSpacing: '0 10px' }}
      >
        <thead className="round-dg__header">
          <tr className="round-dg__row">
            {head.map((h, i) => (
              <th
                key={i}
                className={[
                  'round-dg__cell-header round-dg__cell-header--sm',
                  h.numeric ? 'text-right tabular-nums' : '',
                ].join(' ')}
              >
                {h.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="round-dg__body">
          {rows.map((row, ri) => (
            <tr key={ri} className="round-dg__row">
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={[
                    'round-dg__cell round-dg__cell--sm',
                    head[ci]?.numeric ? 'text-right tabular-nums' : '',
                  ].join(' ')}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="space-y-8 pt-4">
      <Skeleton className="h-20 w-48 rounded-xl" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-32 w-full rounded-md" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-40 w-full rounded-md" />
      </div>
    </div>
  );
}

/* ─── main component ─────────────────────────────────────────── */
export default function OverviewTab({ processId, status }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancel = false;
    const load = () => selectionProcessesAdmin.summary(processId).then(s => !cancel && setSummary(s));
    load();
    if (status === 'open') {
      const t = setInterval(load, 30000);
      return () => {
        cancel = true;
        clearInterval(t);
      };
    }
    return () => {
      cancel = true;
    };
  }, [processId, status]);

  if (!summary) return <OverviewSkeleton />;

  const regionalRows = summary.by_regional.map(r => [r.regional_name, r.count]);

  const institutionRows = summary.by_regional.flatMap(r =>
    r.by_institution.map(i => [r.regional_name, i.institution_name, i.count])
  );

  return (
    <div className="space-y-8 pt-4">
      {/* ── Total stat ── */}
      <div>
        <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-neutral">
          Total de inscritos
        </p>
        <p className="text-4xl font-bold tabular-nums leading-none text-primary-dark">
          {summary.total}
        </p>
      </div>

      {/* ── Por Regional ── */}
      <section>
        <SectionHeading>Por Regional</SectionHeading>
        <SummaryTable
          head={[{ label: 'Regional' }, { label: 'Total', numeric: true }]}
          rows={regionalRows}
          emptyLabel="Nenhuma regional com inscrições"
        />
      </section>

      {/* ── Por Unidade de Ensino ── */}
      <section>
        <SectionHeading>Por Unidade de Ensino</SectionHeading>
        <SummaryTable
          head={[
            { label: 'Regional' },
            { label: 'Unidade de Ensino' },
            { label: 'Total', numeric: true },
          ]}
          rows={institutionRows}
          emptyLabel="Nenhuma unidade com inscrições"
        />
      </section>
    </div>
  );
}
